import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuthState } from '@/shared/stores/auth-store';
import { AuthGuard } from '@/shared/components/AuthGuard';
import { NotificationContainer } from '@/shared/components/NotificationContainer';
import { LoginPage } from '@/pages/LoginPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { DoctorsPage } from '@/pages/DoctorsPage';
import { DoctorCreatePage } from '@/pages/DoctorCreatePage';
import { DoctorEditPage } from '@/pages/DoctorEditPage';
import { PricesPage } from '@/pages/PricesPage';
import { CategoryServicesPage } from '@/pages/CategoryServicesPage';
import { PriceCategoryCreatePage } from '@/pages/PriceCategoryCreatePage';
import { PriceCategoryEditPage } from '@/pages/PriceCategoryEditPage';
import { PriceItemCreatePage } from '@/pages/PriceItemCreatePage';
import { PriceItemEditPage } from '@/pages/PriceItemEditPage';
import { PagesListPage } from '@/pages/PagesListPage';
import { IndexPageSettingsPage } from '@/pages/IndexPageSettingsPage';
import { CustomerInformationPage } from '@/pages/CustomerInformationPage';
import { NotFoundPage } from '@/pages/NotFoundPage';

/**
 * Компонент для обработки редиректа с /login для аутентифицированных пользователей
 * Сохраняет return URL из location.state
 */
const LoginRedirect: React.FC = () => {
  const location = useLocation();
  const from = location.state?.from?.pathname || '/dashboard';
  return <Navigate to={from} replace />;
};

/**
 * Главный компонент приложения админ-панели
 * Управляет маршрутизацией без дублирования проверки аутентификации
 * AuthGuard компоненты сами проверяют аутентификацию при необходимости
 */
export const App: React.FC = () => {
  const { isAuthenticated } = useAuthState();

  return (
    <div className="min-h-screen bg-base-200">
      <Routes>
        {/* Публичные маршруты */}
        <Route
          path="/login"
          element={
            isAuthenticated ? (
              <LoginRedirect />
            ) : (
              <LoginPage />
            )
          }
        />

        {/* Защищенные маршруты */}
        <Route
          path="/"
          element={
            <AuthGuard>
              <Navigate to="/dashboard" replace />
            </AuthGuard>
          }
        />

        <Route
          path="/dashboard"
          element={
            <AuthGuard>
              <DashboardPage />
            </AuthGuard>
          }
        />

        {/* Маршруты врачей */}
        <Route
          path="/doctors"
          element={
            <AuthGuard>
              <DoctorsPage />
            </AuthGuard>
          }
        />

        <Route
          path="/doctors/create"
          element={
            <AuthGuard>
              <DoctorCreatePage />
            </AuthGuard>
          }
        />

        <Route
          path="/doctors/:id/edit"
          element={
            <AuthGuard>
              <DoctorEditPage />
            </AuthGuard>
          }
        />

        {/* Маршруты цен */}
        <Route
          path="/prices"
          element={
            <AuthGuard>
              <PricesPage />
            </AuthGuard>
          }
        />

        <Route
          path="/prices/categories/:categoryId/services"
          element={
            <AuthGuard>
              <CategoryServicesPage />
            </AuthGuard>
          }
        />

        <Route
          path="/prices/categories/create"
          element={
            <AuthGuard>
              <PriceCategoryCreatePage />
            </AuthGuard>
          }
        />

        <Route
          path="/prices/categories/:id/edit"
          element={
            <AuthGuard>
              <PriceCategoryEditPage />
            </AuthGuard>
          }
        />

        <Route
          path="/prices/items/create"
          element={
            <AuthGuard>
              <PriceItemCreatePage />
            </AuthGuard>
          }
        />

        <Route
          path="/prices/items/:id/edit"
          element={
            <AuthGuard>
              <PriceItemEditPage />
            </AuthGuard>
          }
        />

        {/* Маршруты настроек страниц */}
        <Route
          path="/pages"
          element={
            <AuthGuard>
              <PagesListPage />
            </AuthGuard>
          }
        />

        <Route
          path="/pages/index"
          element={
            <AuthGuard>
              <IndexPageSettingsPage />
            </AuthGuard>
          }
        />

        <Route
          path="/pages/customer-information"
          element={
            <AuthGuard>
              <CustomerInformationPage />
            </AuthGuard>
          }
        />

        {/* 404 страница */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>

      {/* Глобальный контейнер уведомлений */}
      <NotificationContainer />
    </div>
  );
};
